import React, { useContext } from 'react';
import { HomepageContext, Hero, HeroAd } from './contexts/contentful';
import Skeleton from 'react-loading-skeleton';

import {
    ShopOnlineOnBonusSection,
    GiftCardsOnBonusSection,
    FeaturedSection,
    CategoriesSection,
    FavoritesSection,
    HeroSection,
    FullAdSection,
} from './sections';
import { CreativeSlotTypes } from 'common/types/CreativeSlotTypes';

interface Map<T> {
    [key: string]: T;
}

const Components: Map<React.ElementType> = {
    categories_component: CategoriesSection,
    favorites_component: FavoritesSection,
    featured_brands_component: FeaturedSection,
    bonus_component: GiftCardsOnBonusSection,
    shop_online_bonus_component: ShopOnlineOnBonusSection,
};

const isAdHero = (hero: Hero | HeroAd) => {
    return (hero as Hero)?.heroLayout === undefined;
};

const getComponentToRender = (
    id: string,
    heroes: Map<Hero | HeroAd>,
    componentMap: Map<string>
) => {
    const itsHeroData = heroes[id];

    if (itsHeroData) {
        return isAdHero(itsHeroData) ? FullAdSection : HeroSection;
    }

    // cross reference the `componentMap` with the ID to retrieve the `component_key` that will be used
    // too reference the `Component` map which maps from component_key -> ReactElement
    return Components[componentMap[id] as string];
};

export const HomePageRenderer = () => {
    const { heroes, componentMap, layout, isLoading } = useContext(HomepageContext);

    // NOTE: Adjust skeletons as needed
    if (isLoading) {
        return (
            <div>
                <Skeleton className="mb-4" height={300} />
                <Skeleton className="mb-4" height={150} />
                <Skeleton className="mb-4" height={200} />
            </div>
        );
    }

    const creativeSlots = [CreativeSlotTypes.P1Hero, CreativeSlotTypes.P2, CreativeSlotTypes.P3];
    return (
        <div>
            {layout.map((id: string) => {
                let creativeSlot;
                if (heroes[id]) {
                    creativeSlot =
                        creativeSlots.length > 1 ? creativeSlots.shift() : creativeSlots[0];
                }
                const creativeSlotProps = creativeSlot ? { creativeSlot } : {};

                const Component = getComponentToRender(id, heroes, componentMap);
                // NOTE: adds a guard in case Component is `undefined`
                return Component ? <Component key={id} id={id} {...creativeSlotProps} /> : null;
            })}
        </div>
    );
};
